import { World } from './World';
import { createLogger } from '../Utils/Logger';
const logger = createLogger('WorldManager');
/**
 * World管理器 - 管理所有World实例
 *
 * WorldManager负责管理多个独立的World实例。
 * 每个World都是独立的ECS环境，可以包含多个Scene。
 *
 * 适用场景：
 * - MMO游戏的多房间管理
 * - 服务器端的多游戏实例
 * - 需要完全隔离的多个游戏环境
 *
 * @example
 * ```typescript
 * // 创建WorldManager实例
 * const worldManager = new WorldManager({
 *     maxWorlds: 100,
 *     autoCleanup: true
 * });
 *
 * // 创建游戏房间World
 * const room1 = worldManager.createWorld('room_001', {
 *     name: 'GameRoom_001',
 *     maxScenes: 5
 * });
 * room1.setActive(true);
 *
 * // 游戏循环
 * function gameLoop(deltaTime: number) {
 *     Core.update(deltaTime);
 *     worldManager.updateAll();  // 更新所有活跃World
 * }
 * ```
 */
export class WorldManager {
    constructor(config = {}) {
        this._worlds = new Map();
        this._isRunning = false;
        this._framesSinceCleanup = 0;
        this._config = {
            maxWorlds: 50,
            autoCleanup: true,
            cleanupFrameInterval: 1800, // 1800帧
            debug: false,
            ...config
        };
        // 默认启动运行状态
        this._isRunning = true;
        logger.info('WorldManager已初始化', {
            maxWorlds: this._config.maxWorlds,
            autoCleanup: this._config.autoCleanup,
            cleanupFrameInterval: this._config.cleanupFrameInterval
        });
    }
    // ===== World管理 =====
    /**
     * 创建新World
     */
    createWorld(worldId, config) {
        if (!worldId || typeof worldId !== 'string' || worldId.trim() === '') {
            throw new Error('World ID不能为空');
        }
        if (this._worlds.has(worldId)) {
            throw new Error(`World ID '${worldId}' 已存在`);
        }
        if (this._worlds.size >= this._config.maxWorlds) {
            throw new Error(`已达到最大World数量限制: ${this._config.maxWorlds}`);
        }
        // 优先级：config.debug > WorldManager.debug > 默认
        const worldConfig = {
            name: worldId,
            debug: config?.debug ?? this._config.debug ?? false,
            ...(config?.maxScenes !== undefined && { maxScenes: config.maxScenes }),
            ...(config?.autoCleanup !== undefined && { autoCleanup: config.autoCleanup })
        };
        const world = new World(worldConfig);
        this._worlds.set(worldId, world);
        return world;
    }
    /**
     * 移除World
     */
    removeWorld(worldId) {
        const world = this._worlds.get(worldId);
        if (!world) {
            return false;
        }
        // 销毁World
        world.destroy();
        this._worlds.delete(worldId);
        logger.info(`移除World: ${worldId}`);
        return true;
    }
    /**
     * 获取World
     */
    getWorld(worldId) {
        return this._worlds.get(worldId) || null;
    }
    /**
     * 获取所有World ID
     */
    getWorldIds() {
        return Array.from(this._worlds.keys());
    }
    /**
     * 获取所有World
     */
    getAllWorlds() {
        return Array.from(this._worlds.values());
    }
    /**
     * 设置World激活状态
     */
    setWorldActive(worldId, active) {
        const world = this._worlds.get(worldId);
        if (!world) {
            logger.warn(`World '${worldId}' 不存在`);
            return;
        }
        if (active) {
            world.start();
            logger.debug(`激活World: ${worldId}`);
        }
        else {
            world.stop();
            logger.debug(`停用World: ${worldId}`);
        }
    }
    /**
     * 检查World是否激活
     */
    isWorldActive(worldId) {
        const world = this._worlds.get(worldId);
        return world?.isActive ?? false;
    }
    // ===== 批量操作 =====
    /**
     * 更新所有活跃的World
     *
     * 应该在每帧的游戏循环中调用。
     * 会自动更新所有活跃World的全局系统和场景。
     *
     * @example
     * ```typescript
     * function gameLoop(deltaTime: number) {
     *     Core.update(deltaTime);      // 更新全局服务
     *     worldManager.updateAll();    // 更新所有World
     * }
     * ```
     */
    updateAll() {
        if (!this._isRunning)
            return;
        for (const world of this._worlds.values()) {
            if (world.isActive) {
                // 更新World的全局System
                world.updateGlobalSystems();
                // 更新World中的所有Scene
                world.updateScenes();
            }
        }
        // 基于帧的自动清理
        if (this._config.autoCleanup) {
            this._framesSinceCleanup++;
            if (this._framesSinceCleanup >= this._config.cleanupFrameInterval) {
                this.cleanup();
                this._framesSinceCleanup = 0; // 重置计数器
                if (this._config.debug) {
                    logger.debug(`执行定期清理World (间隔: ${this._config.cleanupFrameInterval} 帧)`);
                }
            }
        }
    }
    /**
     * 获取所有激活的World
     */
    getActiveWorlds() {
        const activeWorlds = [];
        for (const world of this._worlds.values()) {
            if (world.isActive) {
                activeWorlds.push(world);
            }
        }
        return activeWorlds;
    }
    /**
     * 启动所有World
     */
    startAll() {
        this._isRunning = true;
        for (const world of this._worlds.values()) {
            world.start();
        }
        logger.info('启动所有World');
    }
    /**
     * 停止所有World
     */
    stopAll() {
        this._isRunning = false;
        for (const world of this._worlds.values()) {
            world.stop();
        }
        logger.info('停止所有World');
    }
    /**
     * 查找满足条件的World
     */
    findWorlds(predicate) {
        const results = [];
        for (const world of this._worlds.values()) {
            if (predicate(world)) {
                results.push(world);
            }
        }
        return results;
    }
    /**
     * 根据名称查找World
     */
    findWorldByName(name) {
        for (const world of this._worlds.values()) {
            if (world.name === name) {
                return world;
            }
        }
        return null;
    }
    // ===== 统计和监控 =====
    /**
     * 获取WorldManager统计信息
     */
    getStats() {
        const stats = {
            totalWorlds: this._worlds.size,
            activeWorlds: this.activeWorldCount,
            totalScenes: 0,
            totalEntities: 0,
            totalSystems: 0,
            memoryUsage: 0,
            isRunning: this._isRunning,
            config: { ...this._config },
            worlds: []
        };
        for (const [worldId, world] of this._worlds) {
            const worldStats = world.getStats();
            stats.totalScenes += worldStats.totalSystems; // World的getStats可能需要调整
            stats.totalEntities += worldStats.totalEntities;
            stats.totalSystems += worldStats.totalSystems;
            stats.worlds.push({
                id: worldId,
                name: world.name,
                isActive: world.isActive,
                sceneCount: world.sceneCount,
                ...worldStats
            });
        }
        return stats;
    }
    /**
     * 获取详细状态信息
     */
    getDetailedStatus() {
        return {
            ...this.getStats(),
            worlds: Array.from(this._worlds.entries()).map(([worldId, world]) => ({
                id: worldId,
                isActive: world.isActive,
                status: world.getStatus()
            }))
        };
    }
    // ===== 生命周期管理 =====
    /**
     * 清理空World
     */
    cleanup() {
        const worldsToRemove = [];
        for (const [worldId, world] of this._worlds) {
            if (this.shouldCleanupWorld(world)) {
                worldsToRemove.push(worldId);
            }
        }
        for (const worldId of worldsToRemove) {
            this.removeWorld(worldId);
        }
        if (worldsToRemove.length > 0) {
            logger.debug(`清理了 ${worldsToRemove.length} 个World`);
        }
        return worldsToRemove.length;
    }
    /**
     * 销毁WorldManager
     */
    destroy() {
        logger.info('正在销毁WorldManager...');
        // 停止所有World
        this.stopAll();
        // 销毁所有World
        const worldIds = Array.from(this._worlds.keys());
        for (const worldId of worldIds) {
            this.removeWorld(worldId);
        }
        this._worlds.clear();
        this._isRunning = false;
        logger.info('WorldManager已销毁');
    }
    /**
     * 实现 IService 接口的 dispose 方法
     * 调用 destroy 方法进行清理
     */
    dispose() {
        this.destroy();
    }
    // ===== 私有方法 =====
    /**
     * 判断World是否应该被清理
     * 清理策略：
     * 1. World未激活
     * 2. 没有Scene或所有Scene都是空的
     * 3. 创建时间超过10分钟
     */
    shouldCleanupWorld(world) {
        if (world.isActive) {
            return false;
        }
        const age = Date.now() - world.createdAt;
        const isOldEnough = age > 10 * 60 * 1000; // 10分钟
        if (world.sceneCount === 0) {
            return isOldEnough;
        }
        // 检查是否所有Scene都是空的
        const allScenes = world.getAllScenes();
        const hasEntities = allScenes.some((scene) => scene.entities && scene.entities.count > 0);
        return !hasEntities && isOldEnough;
    }
    // ===== 访问器 =====
    /**
     * 获取World总数
     */
    get worldCount() {
        return this._worlds.size;
    }
    /**
     * 获取激活World数量
     */
    get activeWorldCount() {
        let count = 0;
        for (const world of this._worlds.values()) {
            if (world.isActive)
                count++;
        }
        return count;
    }
    /**
     * 检查是否正在运行
     */
    get isRunning() {
        return this._isRunning;
    }
    /**
     * 获取配置
     */
    get config() {
        return { ...this._config };
    }
}
//# sourceMappingURL=WorldManager.js.map